import React from 'react';

import Container from '@amzn/awsui-components-console/container';
import FormField from '@amzn/awsui-components-console/form-field';
import Select, { SelectProps } from '@amzn/awsui-components-console/select';
import Box from '@amzn/awsui-components-console/box';
import { DateRangePickerProps } from '@amzn/awsui-components-console/date-range-picker';
import Button from '@amzn/awsui-components-console/button';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import Grid from '@amzn/awsui-components-console/grid';

import InfoTooltip from 'src/common/ui/InfoTooltip';

import DateRangeFilter from './DateRangeFilter';

/** Explains what Save Search does. */
const SAVE_SEARCH_INFO =
  'Save your current filter parameters for quick access. Up to 5 searches are ' +
  'kept — the oldest is removed when a 6th is added. Saved searches are stored ' +
  'in this browser only.';

interface TrackFiltersProps {
  namespace: SelectProps.Option | null;
  onNamespaceChange: (option: SelectProps.Option | null) => void;
  stringGroup: SelectProps.Option | null;
  onStringGroupChange: (option: SelectProps.Option | null) => void;
  dateRange: DateRangePickerProps.Value | null;
  onDateRangeChange: (value: DateRangePickerProps.Value | null) => void;
  onApply: () => void;
  /** Opens the naming dialog to persist the current filter selection. */
  onSaveSearch: () => void;
  namespaceOptions: SelectProps.Options;
  stringGroupOptions: SelectProps.Options;
  loading?: boolean;
  stringGroupsLoading?: boolean;
}

const TrackFilters: React.FC<TrackFiltersProps> = ({
  namespace,
  onNamespaceChange,
  stringGroup,
  onStringGroupChange,
  dateRange,
  onDateRangeChange,
  onApply,
  onSaveSearch,
  namespaceOptions,
  stringGroupOptions,
  loading,
  stringGroupsLoading,
}) => (
  <Container>
    <Grid
      gridDefinition={[
        { colspan: { default: 12, s: 3 } },
        { colspan: { default: 12, s: 2 } },
        { colspan: { default: 12, s: 3 } },
        { colspan: { default: 12, s: 4 } },
      ]}
    >
      {/* Namespace selector — required before any data loads */}
      <FormField
        label={
          <span>
            Namespace{' '}
            <Box variant="span" fontSize="body-s" color="text-status-error">
              (Required)
            </Box>
          </span>
        }
      >
        <Select
          selectedOption={namespace}
          onChange={({ detail }) => onNamespaceChange(detail.selectedOption)}
          options={namespaceOptions}
          placeholder="Select a namespace..."
          statusType={loading ? 'loading' : 'finished'}
          loadingText="Loading namespaces"
          filteringType="auto"
        />
      </FormField>

      {/* String group filter — disabled until a namespace is chosen */}
      <FormField label="String Group">
        <Select
          selectedOption={stringGroup}
          onChange={({ detail }) => onStringGroupChange(detail.selectedOption)}
          options={stringGroupOptions}
          placeholder="All"
          disabled={!namespace}
          statusType={stringGroupsLoading ? 'loading' : 'finished'}
          loadingText="Loading string groups"
          filteringType="auto"
        />
      </FormField>

      {/* Date range with 1-year max and no future dates */}
      <DateRangeFilter value={dateRange} onChange={onDateRangeChange} />

      {/* Actions share a single grid column so Search and Save Search sit
          directly next to each other, matching the track.html mockup.
          `top: l` padding bottom-aligns them with the filter inputs. */}
      <Box padding={{ top: 'l' }}>
        <SpaceBetween direction="horizontal" size="xs" alignItems="center">
          <Button variant="primary" onClick={onApply} disabled={!namespace}>
            Search
          </Button>
          {/* Save Search — persists the current selection to localStorage */}
          <Button
            iconName="star"
            onClick={onSaveSearch}
            disabled={!namespace}
            disabledReason="Select a namespace before saving a search"
          >
            Save Search
          </Button>
          <InfoTooltip content={SAVE_SEARCH_INFO} />
        </SpaceBetween>
      </Box>
    </Grid>
  </Container>
);

export default TrackFilters;
